import { useState, useRef, useCallback, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { HiX } from 'react-icons/hi';
import {
  Upload, Calendar, MapPin, PlusCircle, Ticket,
  Globe, Check, Zap, ArrowRight, ArrowLeft,
  Image as ImageIcon, Eye, Sparkles, Trash2, Copy,
  Tag, Users, Info,
  Settings2, Share2, Save, Send, X, Type,
  AlignLeft, Building2, ChevronDown,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import OrganizerSidebar from './OrganizerSidebar';
import OrganizerMobileNavDropdown from './OrganizerMobileNavDropdown';
import { InstaPassLogo } from '../layout/Components/InstaPassLogo';
import StripePayment from '../Payments/StripePayment';
import { publishEvent, EVENT_CREATION_FEE } from './createEventPublish';
import { CREATE_EVENT_CATEGORIES, findCreateEventCategory } from './createEventCategories';
import {
  getCurrentTypes,
  getDefaultFormData,
  createEmptyTicket,
  canAdvanceStep,
  getAdvanceBlockReason,
} from './createEventFormHelpers';


/* ═══════════════════════════════════════════════════════════════
   CREATE EVENT — Premium 5-Step Wizard with Live Preview
   Inspired by Eventbrite + Tixr UX, reengineered for InstaPass
   ═══════════════════════════════════════════════════════════════ */

const STEPS = [
  { id: 'basics', label: 'Basic Info', icon: Type, desc: 'Name, category & description' },
  { id: 'datetime', label: 'Date & Venue', icon: Calendar, desc: 'When and where' },
  { id: 'tickets', label: 'Tickets', icon: Ticket, desc: 'Pricing & tiers' },
  { id: 'media', label: 'Media', icon: ImageIcon, desc: 'Images & branding' },
  { id: 'publish', label: 'Publish', icon: Send, desc: 'Review & go live' },
];

/** Must be module-scoped — defining inside the wizard remounts inputs every keystroke. */
function FormInput({ label, required, ...props }) {
  return (
    <div className="w-full">
      {label && (
        <label className="flex items-center gap-1 text-[11px] uppercase tracking-[0.08em] text-white/40 mb-2" style={{ fontWeight: 700 }}>
          {label}
          {required && <span className="text-red-400">*</span>}
        </label>
      )}
      <input
        className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-[14px] placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40 transition-all"
        {...props}
      />
    </div>
  );
}

const DRAFT_KEY = 'instapass_event_draft';

function saveDraft(data) {
  try {
    // Files and blob previews can't be stored; the cover is re-picked on return.
    const { eventImage, eventImagePreview, ...rest } = data || {};
    localStorage.setItem(DRAFT_KEY, JSON.stringify(rest));
    return true;
  } catch (_) {
    return false;
  }
}

export default function CreateEventWizard({ initialStep = 0 }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState(initialStep);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const contentRef = useRef(null);
  const coverInputRef = useRef(null);

  const [formData, setFormData] = useState(() => {
    if (location.state?.formData) return getDefaultFormData(location.state.formData);
    let draft = {};
    try {
      draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || '{}') || {};
    } catch (_) {
      draft = {};
    }
    return getDefaultFormData(draft);
  });
  const patchForm = (updates) => setFormData((prev) => ({ ...prev, ...updates }));

  useEffect(() => {
    if (location.state?.formData) {
      setFormData((prev) => ({ ...prev, ...location.state.formData }));
    }
  }, [location.state]);

  useEffect(() => {
    return () => {
      if (formData.eventImagePreview?.startsWith('blob:')) {
        URL.revokeObjectURL(formData.eventImagePreview);
      }
    };
  }, [formData.eventImagePreview]);

  useEffect(() => {
    const t = setTimeout(() => saveDraft(formData), 600);
    return () => clearTimeout(t);
  }, [formData]);

  const createEventApi = useCallback(async () => publishEvent(formData), [formData]);

  const handlePaymentCompleted = useCallback(async () => {
    setIsPublishing(true);
    setPaymentError('');
    try {
      const result = await createEventApi();
      setShowPaymentModal(false);
      try { localStorage.removeItem(DRAFT_KEY); } catch (_) {}
      toast.success('Event published! Share your link to start getting guests.');
      const newId = result?.event?._id;
      navigate(newId ? `/tickets/group.details/${newId}` : '/organizer/check-in');
    } catch (error) {
      toast.error(error.message || 'Failed to publish event');
    } finally {
      setIsPublishing(false);
    }
  }, [createEventApi, navigate]);

  // Publishing is free (the $50 creation fee is gone; InstaPass earns per ticket sold).
  const handlePublish = () => {
    setPaymentError('');
    handlePaymentCompleted();
  };

  const eventName = formData.eventTitle;
  const setEventName = (v) => patchForm({ eventTitle: typeof v === 'string' ? v : v?.target?.value ?? '' });
  const category = formData.category;
  const setCategory = (v) => patchForm({ category: v, type: '' });
  const description = formData.eventDescription;
  const setDescription = (v) => patchForm({ eventDescription: typeof v === 'string' ? v : v?.target?.value ?? '' });
  const tags = formData.tags || [];
  const setTags = (v) => patchForm({ tags: v });
  const eventDate = formData.startDate;
  const setEventDate = (v) => patchForm({ startDate: typeof v === 'string' ? v : v?.target?.value ?? '' });
  const startTime = formData.startTime;
  const setStartTime = (v) => patchForm({ startTime: typeof v === 'string' ? v : v?.target?.value ?? '' });
  const endTime = formData.endTime;
  const setEndTime = (v) => patchForm({ endTime: typeof v === 'string' ? v : v?.target?.value ?? '' });
  const setEndDate = (v) => patchForm({ endDate: typeof v === 'string' ? v : v?.target?.value ?? '' });
  const venue = formData.venueName;
  const setVenue = (v) => patchForm({ venueName: typeof v === 'string' ? v : v?.target?.value ?? '' });
  const address = formData.venueAddress;
  const setAddress = (v) => patchForm({ venueAddress: typeof v === 'string' ? v : v?.target?.value ?? '' });
  const city = formData.city || '';
  const setCity = (v) => patchForm({ city: typeof v === 'string' ? v : v?.target?.value ?? '' });
  const stateRegion = formData.stateRegion || '';
  const setStateRegion = (v) => patchForm({ stateRegion: typeof v === 'string' ? v : v?.target?.value ?? '' });
  const zipCode = formData.zipCode || '';
  const setZipCode = (v) => patchForm({ zipCode: typeof v === 'string' ? v : v?.target?.value ?? '' });
  const isOnline = formData.eventFormat === 'online';
  const setIsOnline = (online) => patchForm({ eventFormat: online ? 'online' : 'physical' });
  const eventVisibility = formData.eventVisibility || 'public';
  const setEventVisibility = (v) => patchForm({ eventVisibility: v });
  const coverImage = formData.eventImagePreview || '';

  const handleCoverImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload a PNG or JPG image.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be 10MB or smaller.');
      return;
    }
    if (formData.eventImagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(formData.eventImagePreview);
    }
    patchForm({
      eventImage: file,
      eventImagePreview: URL.createObjectURL(file),
    });
  };

  const clearCoverImage = () => {
    if (formData.eventImagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(formData.eventImagePreview);
    }
    patchForm({ eventImage: null, eventImagePreview: '' });
    if (coverInputRef.current) coverInputRef.current.value = '';
  };
  const tiers = formData.tickets || [];
  const setTiers = (v) =>
    setFormData((prev) => ({
      ...prev,
      tickets: typeof v === 'function' ? v(prev.tickets || []) : v,
    }));

  // ── Helpers ──
  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t) && tags.length < 8) {
      setTags([...tags, t]);
      setTagInput('');
    }
  };

  const addTier = () => { setTiers([...(formData.tickets || []), createEmptyTicket()]); };

  const updateTier = (id, updates) => {
    setTiers((prev) => prev.map((t) => (t.id === id ? { ...t, ...updates } : t)));
  };

  const removeTier = (id) => {
    if ((formData.tickets || []).length > 1) setTiers((prev) => prev.filter((t) => t.id !== id));
  };

  const duplicateTier = (id) => {
    const ticket = (formData.tickets || []).find((t) => t.id === id);
    if (ticket) setTiers([...(formData.tickets || []), { ...ticket, id: Date.now(), ticketName: `${ticket.ticketName || ''} (Copy)` }]);
  };

  const canAdvance = () => canAdvanceStep(step, formData);

  useEffect(() => {
    if (step === 2 && (!formData.tickets || formData.tickets.length === 0)) {
      setTiers([createEmptyTicket()]);
    }
  }, [step]);

  const goNext = () => {
    if (step >= STEPS.length - 1) return;
    if (!canAdvance()) {
      toast.error(getAdvanceBlockReason(step, formData));
      return;
    }
    setStep(step + 1);
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const goBack = () => { if (step > 0) setStep(step - 1); };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      if (!saveDraft(formData)) throw new Error('storage');
      toast.success('Draft saved on this device. Come back any time to finish.');
      navigate('/');
    } catch {
      toast.error('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };


  const totalCapacity = (formData.tickets || []).reduce((s, t) => s + (parseInt(t.quantity, 10) || 0), 0);
  const priceRange = tiers.length > 0
    ? tiers.every(t => (parseFloat(t.price) || 0) === 0)
      ? 'Free'
      : tiers.some(t => (parseFloat(t.price) || 0) === 0)
        ? `Free – $${Math.max(...tiers.filter(t => (parseFloat(t.price) || 0) > 0).map(t => t.price))}`
        : `$${Math.min(...tiers.map(t => t.price))} – $${Math.max(...tiers.map(t => t.price))}`
    : '$0';

  const selectedCategory = findCreateEventCategory(category);

  return (
    <div className="min-h-screen bg-[#060D1B] overflow-x-hidden" style={{ fontFamily: "'Outfit', 'Inter', sans-serif" }}>
      <OrganizerSidebar />

      <div className="ml-0 lg:ml-[260px] flex flex-col h-screen min-h-0 min-w-0">

        {/* ═══ TOP HEADER BAR ═══ */}
        <header className="shrink-0 border-b border-[#1E293B]/60 bg-[#060D1B]/95 backdrop-blur-lg z-30">
          {/* Mobile header */}
          <div className="lg:hidden flex flex-col px-3 sm:px-4 py-2 gap-2">
            <div className="w-full grid grid-cols-[1fr_auto_1fr] items-center gap-2">
              <OrganizerMobileNavDropdown className="min-w-0 justify-self-start" />
              <Link to="/" className="justify-self-center shrink-0" aria-label="InstaPass home">
                <InstaPassLogo size="sm" />
              </Link>
              <div className="justify-self-end w-10" aria-hidden="true" />
            </div>

            <div className="flex flex-col gap-2 w-full min-w-0">
              <div className="flex items-center gap-2 min-w-0 w-full">
                <button
                  type="button"
                  onClick={() => navigate('/create-event')}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white/40 hover:text-white hover:bg-white/5 transition-all"
                  aria-label="Back"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <h1
                  className="flex-1 min-w-0 truncate text-[13px] sm:text-[15px] text-white"
                  style={{ fontWeight: 700 }}
                >
                  {eventName || 'Untitled Event'}
                </h1>
                <span
                  className="shrink-0 px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-400 text-[9px] sm:text-[10px] uppercase tracking-wider"
                  style={{ fontWeight: 700 }}
                >
                  Draft
                </span>
              </div>

              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 w-full justify-end">
                <button
                  type="button"
                  onClick={() => setShowPreview(!showPreview)}
                  aria-label={showPreview ? 'Hide preview' : 'Show preview'}
                  className={`flex h-9 w-9 sm:h-auto sm:w-auto items-center justify-center sm:gap-2 sm:px-3.5 sm:py-2 rounded-xl text-[12px] transition-all ${
                    showPreview
                      ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                      : 'text-white/40 hover:text-white/60 border border-transparent hover:bg-white/5'
                  }`}
                  style={{ fontWeight: 600 }}
                >
                  <Eye className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Preview</span>
                </button>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  aria-label={saving ? 'Saving draft' : 'Save draft'}
                  className="flex h-9 w-9 sm:h-auto sm:w-auto items-center justify-center sm:gap-2 sm:px-3.5 sm:py-2 rounded-xl text-[12px] text-white/40 hover:text-white/60 hover:bg-white/5 border border-transparent transition-all"
                  style={{ fontWeight: 600 }}
                >
                  <Save className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{saving ? 'Saving...' : 'Save Draft'}</span>
                </button>
                <button
                  type="button"
                  onClick={handlePublish}
                  aria-label="Publish event"
                  className="flex h-9 items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 rounded-xl bg-[#E52324] text-white text-[11px] sm:text-[12px] hover:bg-[#c91f20] transition-colors shadow-lg shadow-[#E52324]/20 shrink-0"
                  style={{ fontWeight: 700, letterSpacing: '0.04em' }}
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>Publish</span>
                </button>
              </div>
            </div>
          </div>

          {/* Desktop header — original single-row layout */}
          <div className="hidden lg:flex items-center justify-between h-[64px] px-4 sm:px-6 w-full min-w-0">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <button
                type="button"
                onClick={() => navigate('/create-event')}
                className="flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-white/40 hover:text-white hover:bg-white/5 transition-all text-[12px]"
                style={{ fontWeight: 600 }}
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <h1 className="text-[15px] sm:text-[16px] text-white truncate max-w-[280px] xl:max-w-none" style={{ fontWeight: 700 }}>
                {eventName || 'Untitled Event'}
              </h1>
              <span className="px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-400 text-[10px] uppercase tracking-wider" style={{ fontWeight: 700 }}>
                Draft
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-[12px] transition-all ${
                  showPreview
                    ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                    : 'text-white/40 hover:text-white/60 border border-transparent hover:bg-white/5'
                }`}
                style={{ fontWeight: 600 }}
              >
                <Eye className="w-3.5 h-3.5" /> Preview
              </button>
              <button
                type="button"
                onClick={handleSaveDraft}
                className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-[12px] text-white/40 hover:text-white/60 hover:bg-white/5 border border-transparent transition-all"
                style={{ fontWeight: 600 }}
              >
                <Save className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                type="button"
                onClick={handlePublish}
                className="flex items-center gap-2 px-5 py-2 rounded-xl bg-[#E52324] text-white text-[12px] hover:bg-[#c91f20] transition-colors shadow-lg shadow-[#E52324]/20"
                style={{ fontWeight: 700, letterSpacing: '0.04em' }}
              >
                <Send className="w-3.5 h-3.5" /> Publish
              </button>
            </div>
          </div>
        </header>

        {/* ═══ STEP PROGRESS BAR ═══ */}
        <div className="shrink-0 border-b border-[#1E293B]/40 bg-[#0A1628]/60 overflow-x-auto lg:overflow-visible">
          <div className="flex items-center px-2 sm:px-4 lg:px-6 py-0 min-w-0">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === step;
              const isComplete = i < step;
              return (
                <button
                  key={s.id}
                  onClick={() => (isComplete || i <= step + 1) && setStep(i)}
                  className="flex-1 group relative"
                >
                  <div className={`flex items-center justify-center lg:justify-start gap-2 sm:gap-3 px-1 sm:px-4 lg:px-4 py-2.5 sm:py-4 lg:py-4 transition-all ${isActive ? '' : 'opacity-50 hover:opacity-80'}`}>
                    <div className={`w-7 h-7 sm:w-8 lg:w-8 rounded-lg flex items-center justify-center shrink-0 transition-all ${
                      isComplete ? 'bg-emerald-500/15 text-emerald-400' :
                      isActive ? 'bg-blue-500/15 text-blue-400' :
                      'bg-white/5 text-white/30'
                    }`}>
                      {isComplete ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <div className="text-left hidden xl:block min-w-0">
                      <div className={`text-[12px] ${isActive ? 'text-white' : 'text-white/50'}`} style={{ fontWeight: 700 }}>
                        {s.label}
                      </div>
                      <div className="text-[10px] text-white/25">{s.desc}</div>
                    </div>
                  </div>
                  {/* Active indicator */}
                  {isActive && (
                    <div
                      layoutId="stepIndicator"
                      className="absolute bottom-0 left-2 right-2 h-[2px] bg-blue-500 rounded-full"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ═══ MAIN CONTENT AREA ═══ */}
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* ── Form Panel ── */}
          <div ref={contentRef} className="flex-1 min-w-0 overflow-y-auto">
            <div
              className={`p-6 sm:p-8 lg:p-10 max-w-[800px] ${showPreview ? '' : 'mx-auto'}`}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                >
                  {/* ──────────────────────────────────────────
                      STEP 0: BASIC INFO
                      ────────────────────────────────────────── */}
                  {step === 0 && (
                    <div className="space-y-8">
                      <div>
                        <h2 className="text-[24px] text-white mb-1" style={{ fontWeight: 800 }}>
                          Let's start with the basics
                        </h2>
                        <p className="text-[14px] text-white/35">
                          Give your event a name and tell people what it's about.
                        </p>
                      </div>

                      {/* Event Name */}
                      <div>
                        <FormInput
                          label="Event Name"
                          required
                          placeholder="e.g. Summer Music Festival 2026"
                          value={eventName}
                          onChange={(e) => setEventName(e.target.value)}
                          maxLength={100}
                        />
                        <div className="flex justify-end mt-1.5">
                          <span className={`text-[10px] ${eventName.length > 80 ? 'text-amber-400' : 'text-white/20'}`}>
                            {eventName.length}/100
                          </span>
                        </div>
                      </div>

                      {/* Category */}
                      <div>
                        <label className="flex items-center gap-1 text-[11px] uppercase tracking-[0.08em] text-white/40 mb-3" style={{ fontWeight: 700 }}>
                          Category <span className="text-red-400">*</span>
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {CREATE_EVENT_CATEGORIES.map((cat) => {
                            const CatIcon = cat.icon;
                            const selected =
                              category === cat.id || cat.legacyIds?.includes(category);
                            return (
                              <button
                                key={cat.id}
                                type="button"
                                onClick={() => setCategory(cat.id)}
                                className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all ${
                                  selected
                                    ? 'border-blue-500/40 bg-blue-500/[0.08]'
                                    : 'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/[0.10]'
                                }`}
                              >
                                <div
                                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                                  style={{ backgroundColor: `${cat.color}15` }}
                                >
                                  <CatIcon className="w-5 h-5" style={{ color: cat.color }} />
                                </div>
                                <span className={`text-[11px] ${selected ? 'text-white' : 'text-white/50'}`} style={{ fontWeight: 600 }}>
                                  {cat.label}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {category && (
                        <div>
                          <label className="flex items-center gap-1 text-[11px] uppercase tracking-[0.08em] text-white/40 mb-2" style={{ fontWeight: 700 }}>
                            Type <span className="text-red-400">*</span>
                          </label>
                          <select
                            value={formData.type || ''}
                            onChange={(e) => patchForm({ type: e.target.value })}
                            className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40 transition-all"
                          >
                            <option value="" className="bg-[#111827]">Select type</option>
                            {getCurrentTypes(formData).map((opt) => (
                              <option key={opt.value} value={opt.value} className="bg-[#111827]">
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Description */}
                      <div>
                        <label className="flex items-center gap-1 text-[11px] uppercase tracking-[0.08em] text-white/40 mb-2" style={{ fontWeight: 700 }}>
                          <AlignLeft className="w-3 h-3" /> Description
                        </label>
                        <textarea
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="Tell attendees what makes this event special..."
                          rows={5}
                          className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-[14px] placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40 transition-all resize-none"
                        />
                        <div className="flex justify-end mt-1.5">
                          <span
                            className={`text-[10px] ${
                              (description?.trim().length ?? 0) < 10 ? 'text-amber-400' : 'text-white/20'
                            }`}
                          >
                            {(description?.trim().length ?? 0)}/10 min characters
                          </span>
                        </div>
                      </div>

                      {/* Tags */}
                      <div>
                        <label className="flex items-center gap-1 text-[11px] uppercase tracking-[0.08em] text-white/40 mb-2" style={{ fontWeight: 700 }}>
                          <Tag className="w-3 h-3" /> Tags
                          <span className="text-white/20 normal-case tracking-normal ml-1">(optional, up to 8)</span>
                        </label>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {tags.map(tag => (
                            <span key={tag} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/10 text-blue-400 text-[12px]" style={{ fontWeight: 600 }}>
                              #{tag}
                              <button onClick={() => setTags(tags.filter(t => t !== tag))} className="hover:text-white transition-colors">
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <input
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                            placeholder="Add a tag..."
                            className="flex-1 px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-[13px] placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40 transition-all"
                          />
                          <button
                            onClick={addTag}
                            className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/[0.08] text-white/50 text-[12px] hover:bg-white/10 transition-all"
                            style={{ fontWeight: 600 }}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ──────────────────────────────────────────
                      STEP 1: DATE & VENUE
                      ────────────────────────────────────────── */}
                  {step === 1 && (
                    <div className="space-y-8">
                      <div>
                        <h2 className="text-[24px] text-white mb-1" style={{ fontWeight: 800 }}>
                          When and where
                        </h2>
                        <p className="text-[14px] text-white/35">
                          Set the date, time, and location for your event.
                        </p>
                      </div>

                      {/* Date & Time */}
                      <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
                        <div className="flex items-center gap-2 mb-5">
                          <Calendar className="w-4 h-4 text-blue-400" />
                          <span className="text-[13px] text-white" style={{ fontWeight: 700 }}>Date & Time</span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                          <FormInput
                            label="Start Date"
                            required
                            type="date"
                            value={eventDate}
                            onChange={(e) => setEventDate(e.target.value)}
                          />
                          <FormInput
                            label="Start Time"
                            required
                            type="time"
                            value={startTime}
                            onChange={(e) => setStartTime(e.target.value)}
                          />
                          <FormInput
                            label="End Date"
                            required
                            type="date"
                            value={formData.endDate || ''}
                            onChange={(e) => setEndDate(e.target.value)}
                          />
                          <FormInput
                            label="End Time"
                            required
                            type="time"
                            value={endTime}
                            onChange={(e) => setEndTime(e.target.value)}
                          />
                        </div>
                      </div>

                      {/* Online / In-Person Toggle */}
                      <div className="flex gap-3">
                        <button
                          onClick={() => setIsOnline(false)}
                          className={`flex-1 flex items-center gap-3 px-5 py-4 rounded-xl border transition-all ${
                            !isOnline ? 'border-blue-500/40 bg-blue-500/[0.08]' : 'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]'
                          }`}
                        >
                          <MapPin className={`w-5 h-5 ${!isOnline ? 'text-blue-400' : 'text-white/30'}`} />
                          <div className="text-left">
                            <div className={`text-[13px] ${!isOnline ? 'text-white' : 'text-white/50'}`} style={{ fontWeight: 700 }}>In-Person</div>
                            <div className="text-[11px] text-white/25">Physical venue</div>
                          </div>
                        </button>
                        <button
                          onClick={() => setIsOnline(true)}
                          className={`flex-1 flex items-center gap-3 px-5 py-4 rounded-xl border transition-all ${
                            isOnline ? 'border-blue-500/40 bg-blue-500/[0.08]' : 'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]'
                          }`}
                        >
                          <Globe className={`w-5 h-5 ${isOnline ? 'text-blue-400' : 'text-white/30'}`} />
                          <div className="text-left">
                            <div className={`text-[13px] ${isOnline ? 'text-white' : 'text-white/50'}`} style={{ fontWeight: 700 }}>Online</div>
                            <div className="text-[11px] text-white/25">Virtual event</div>
                          </div>
                        </button>
                      </div>

                      {/* Venue Details */}
                      {!isOnline ? (
                        <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
                          <div className="flex items-center gap-2 mb-5">
                            <Building2 className="w-4 h-4 text-blue-400" />
                            <span className="text-[13px] text-white" style={{ fontWeight: 700 }}>Venue Details</span>
                          </div>
                          <div className="space-y-4">
                            <FormInput
                              label="Venue Name"
                              required
                              placeholder="e.g. Madison Square Garden"
                              value={venue}
                              onChange={(e) => setVenue(e.target.value)}
                            />
                            <FormInput
                              label="Street Address"
                              placeholder="123 Main Street"
                              value={address}
                              onChange={(e) => setAddress(e.target.value)}
                            />
                            <div className="grid grid-cols-3 gap-4">
                              <FormInput
                                label="City"
                                placeholder="New York"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                              />
                              <FormInput
                                label="State"
                                placeholder="NY"
                                value={stateRegion}
                                onChange={(e) => setStateRegion(e.target.value)}
                              />
                              <FormInput
                                label="ZIP"
                                placeholder="10001"
                                value={zipCode}
                                onChange={(e) => setZipCode(e.target.value)}
                              />
                            </div>
                          </div>

                          {/* Map Placeholder */}
                          <div className="mt-5 h-[160px] rounded-xl bg-[#0B1220] border border-[#1F2937] flex items-center justify-center">
                            <div className="text-center">
                              <MapPin className="w-8 h-8 text-white/10 mx-auto mb-2" />
                              <span className="text-[11px] text-white/20">Map preview will appear here</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
                          <div className="flex items-center gap-2 mb-5">
                            <Globe className="w-4 h-4 text-blue-400" />
                            <span className="text-[13px] text-white" style={{ fontWeight: 700 }}>Online Event</span>
                          </div>
                          <FormInput
                            label="Streaming URL"
                            placeholder="https://zoom.us/j/..."
                            value={formData.eventLink || ''}
                            onChange={(e) => patchForm({ eventLink: e.target.value })}
                          />
                          <p className="text-[11px] text-white/20 mt-2">
                            This link will be shared with ticket holders after purchase.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ──────────────────────────────────────────
                      STEP 2: TICKETS
                      ────────────────────────────────────────── */}
                  {step === 2 && (
                    <div className="space-y-8">
                      <div className="flex items-start justify-between">
                        <div>
                          <h2 className="text-[24px] text-white mb-1" style={{ fontWeight: 800 }}>
                            Ticket Tiers
                          </h2>
                          <p className="text-[14px] text-white/35">
                            Set up pricing, quantity, and tier details.
                          </p>
                        </div>
                        <button
                          onClick={addTier}
                          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-500/10 text-blue-400 text-[12px] border border-blue-500/20 hover:bg-blue-500/15 transition-all"
                          style={{ fontWeight: 700 }}
                        >
                          <PlusCircle className="w-3.5 h-3.5" /> Add Tier
                        </button>
                      </div>

                      {/* Quick Stats */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                          <div className="text-[10px] text-white/30 uppercase tracking-wider mb-1" style={{ fontWeight: 700 }}>Total Capacity</div>
                          <div className="text-[20px] text-white" style={{ fontWeight: 800 }}>{totalCapacity.toLocaleString()}</div>
                        </div>
                        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                          <div className="text-[10px] text-white/30 uppercase tracking-wider mb-1" style={{ fontWeight: 700 }}>Price Range</div>
                          <div className="text-[20px] text-white" style={{ fontWeight: 800 }}>{priceRange}</div>
                        </div>
                        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                          <div className="text-[10px] text-white/30 uppercase tracking-wider mb-1" style={{ fontWeight: 700 }}>Tiers</div>
                          <div className="text-[20px] text-white" style={{ fontWeight: 800 }}>{tiers.length}</div>
                        </div>
                      </div>

                      {/* Tier Cards */}
                      <div className="space-y-4">
                        {tiers.map((tier, idx) => (
                          <motion.div
                            key={tier.id}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.2, delay: idx * 0.05 }}
                            className="relative p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.10] transition-all group"
                          >
                            {/* Tier header + actions */}
                            <div className="flex items-center justify-between mb-4">
                              <span
                                className="text-[11px] uppercase tracking-wider text-white/35"
                                style={{ fontWeight: 700 }}
                              >
                                Tier {idx + 1}
                              </span>
                              <div className="flex items-center gap-1 sm:opacity-100 opacity-100">
                                <button
                                  type="button"
                                  onClick={() => duplicateTier(tier.id)}
                                  className="p-2 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/5 transition-all"
                                  title="Duplicate"
                                >
                                  <Copy className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeTier(tier.id)}
                                  className="p-2 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-400/10 transition-all"
                                  title="Delete"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>

                            <div className="mb-5">
                              <label
                                htmlFor={`tier-name-${tier.id}`}
                                className="text-[10px] uppercase tracking-wider text-white/30 mb-1.5 block"
                                style={{ fontWeight: 700 }}
                              >
                                Tier Name <span className="text-red-400">*</span>
                              </label>
                              <input
                                id={`tier-name-${tier.id}`}
                                type="text"
                                value={tier.ticketName}
                                onChange={(e) => updateTier(tier.id, { ticketName: e.target.value })}
                                placeholder="e.g., General Admission, VIP, Early Bird"
                                className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40 transition-all placeholder:text-white/25"
                                style={{ fontWeight: 600 }}
                              />
                            </div>

                            {/* Type selector */}
                            <div className="flex gap-2 mb-5">
                              {(['paid', 'free']).map(t => (
                                <button
                                  key={t}
                                  type="button"
                                  onClick={() =>
                                    updateTier(tier.id, {
                                      price: t === 'free' ? '0' : tier.price && tier.price !== '0' ? tier.price : '25',
                                    })
                                  }
                                  className={`px-3.5 py-1.5 rounded-lg text-[11px] border transition-all ${
                                    (t === 'free' ? (parseFloat(tier.price)||0)===0 : (parseFloat(tier.price)||0) > 0)
                                      ? 'bg-blue-500/10 text-blue-400 border-blue-500/30'
                                      : 'text-white/30 border-white/[0.06] hover:text-white/50 hover:bg-white/[0.03]'
                                  }`}
                                  style={{ fontWeight: 700, textTransform: 'capitalize' }}
                                >
                                  {t}
                                </button>
                              ))}
                            </div>

                            {/* Price & Quantity */}
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div>
                                <label className="text-[10px] uppercase tracking-wider text-white/30 mb-1.5 block" style={{ fontWeight: 700 }}>Price</label>
                                {(parseFloat(tier.price) || 0) === 0 ? (
                                  <div className="w-full px-4 py-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[14px] flex items-center gap-2" style={{ fontWeight: 700 }}>
                                    <Check className="w-4 h-4" />
                                    FREE
                                  </div>
                                ) : (
                                  <div className="relative">
                                    <span className="pointer-events-none absolute left-4 inset-y-0 flex items-center text-white/30 text-[14px] leading-none">
                                      $
                                    </span>
                                    <input
                                      type="number"
                                      min={0}
                                      value={tier.price}
                                      onChange={(e) => updateTier(tier.id, { price: e.target.value })}
                                      className="w-full pl-8 pr-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-[14px] leading-normal focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all"
                                    />
                                  </div>
                                )}
                              </div>
                              <div>
                                <label className="text-[10px] uppercase tracking-wider text-white/30 mb-1.5 block" style={{ fontWeight: 700 }}>Quantity Available</label>
                                <input
                                  type="number"
                                  min={1}
                                  value={tier.quantity}
                                  onChange={(e) => updateTier(tier.id, { quantity: e.target.value })}
                                  className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all"
                                />
                              </div>
                            </div>

                            {/* Tier description / per-order limits — not sent to API yet
                            <div>
                              <label className="text-[10px] uppercase tracking-wider text-white/30 mb-1.5 block" style={{ fontWeight: 700 }}>Tier Description</label>
                              <input
                                value={tier.description}
                                onChange={(e) => updateTier(tier.id, { description: e.target.value })}
                                placeholder="What's included?"
                                className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-[13px] placeholder:text-white/15 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-4 mt-4">...</div>
                            */}
                          </motion.div>
                        ))}
                      </div>

                      {/* Add Tier Button (bottom) */}
                      <button
                        onClick={addTier}
                        className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl border-2 border-dashed border-white/[0.08] text-white/30 text-[13px] hover:border-blue-500/30 hover:text-blue-400 hover:bg-blue-500/[0.03] transition-all"
                        style={{ fontWeight: 600 }}
                      >
                        <PlusCircle className="w-4 h-4" /> Add Another Ticket Tier
                      </button>
                    </div>
                  )}

                  {/* ──────────────────────────────────────────
                      STEP 3: MEDIA
                      ────────────────────────────────────────── */}
                  {step === 3 && (
                    <div className="space-y-8">
                      <div>
                        <h2 className="text-[24px] text-white mb-1" style={{ fontWeight: 800 }}>
                          Event Media
                        </h2>
                        <p className="text-[14px] text-white/35">
                          Add a cover image to make your event stand out.
                        </p>
                      </div>

                      {/* Cover Image */}
                      <div>
                        <label className="flex items-center gap-1 text-[11px] uppercase tracking-[0.08em] text-white/40 mb-3" style={{ fontWeight: 700 }}>
                          Cover Image <span className="text-red-400">*</span>
                        </label>
                        <input
                          ref={coverInputRef}
                          id="create-event-cover-input"
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                          className="sr-only"
                          onChange={handleCoverImageSelect}
                        />
                        {coverImage ? (
                          <div className="relative rounded-2xl overflow-hidden group">
                            <img src={coverImage} alt="Cover" className="w-full aspect-[21/9] object-cover" />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                              <button
                                type="button"
                                onClick={clearCoverImage}
                                className="px-4 py-2 rounded-xl bg-red-500/80 text-white text-[12px] hover:bg-red-500 transition-all"
                                style={{ fontWeight: 700 }}
                              >
                                <Trash2 className="w-3.5 h-3.5 inline mr-1.5" /> Remove
                              </button>
                              <button
                                type="button"
                                onClick={() => coverInputRef.current?.click()}
                                className="px-4 py-2 rounded-xl bg-white/20 text-white text-[12px] hover:bg-white/30 transition-all"
                                style={{ fontWeight: 700 }}
                              >
                                Replace
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => coverInputRef.current?.click()}
                            className="w-full aspect-[21/9] rounded-2xl border-2 border-dashed border-white/[0.08] bg-white/[0.01] flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-blue-500/30 hover:bg-blue-500/[0.02] transition-all group"
                          >
                            <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center group-hover:bg-blue-500/10 transition-all">
                              <Upload className="w-7 h-7 text-white/20 group-hover:text-blue-400 transition-colors" />
                            </div>
                            <div className="text-center">
                              <p className="text-[13px] text-white/40" style={{ fontWeight: 600 }}>Click to upload cover image</p>
                              <p className="text-[11px] text-white/20 mt-0.5">Recommended: 2160×924px · PNG or JPG · Max 10MB</p>
                            </div>
                          </button>
                        )}
                      </div>

                      {/* Event visibility — not wired to API yet
                      <div>...</div>
                      */}

                      {/* Additional settings (refunds, scarcity, age) — not wired to API yet
                      <div className="p-6 rounded-2xl ...">...</div>
                      */}
                    </div>
                  )}

                  {/* ──────────────────────────────────────────
                      STEP 4: REVIEW & PUBLISH
                      ────────────────────────────────────────── */}
                  {step === 4 && (
                    <div className="space-y-8">
                      <div>
                        <h2 className="text-[24px] text-white mb-1" style={{ fontWeight: 800 }}>
                          Review & Publish
                        </h2>
                        <p className="text-[14px] text-white/35">
                          Everything looks good? Let's go live.
                        </p>
                      </div>

                      {/* Summary Cards */}
                      <div className="space-y-4">
                        {/* Event Info */}
                        <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                              <Type className="w-4 h-4 text-blue-400" />
                              <span className="text-[13px] text-white" style={{ fontWeight: 700 }}>Event Info</span>
                            </div>
                            <button onClick={() => setStep(0)} className="text-[11px] text-blue-400 hover:text-blue-300 transition-colors" style={{ fontWeight: 600 }}>
                              Edit
                            </button>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <div className="text-[10px] text-white/25 uppercase tracking-wider mb-0.5" style={{ fontWeight: 700 }}>Event Name</div>
                              <div className="text-[15px] text-white" style={{ fontWeight: 700 }}>{eventName || 'Not set'}</div>
                            </div>
                            <div className="flex gap-8">
                              <div>
                                <div className="text-[10px] text-white/25 uppercase tracking-wider mb-0.5" style={{ fontWeight: 700 }}>Category</div>
                                <div className="text-[13px] text-white/70">{selectedCategory?.label || 'Not set'}</div>
                              </div>
                              {/* Visibility not sent to API yet
                              <div>
                                <div className="text-[10px] text-white/25 uppercase tracking-wider mb-0.5" style={{ fontWeight: 700 }}>Visibility</div>
                                <div className="text-[13px] text-white/70 capitalize">{eventVisibility}</div>
                              </div>
                              */}
                            </div>
                            {description && (
                              <div>
                                <div className="text-[10px] text-white/25 uppercase tracking-wider mb-0.5" style={{ fontWeight: 700 }}>Description</div>
                                <div className="text-[13px] text-white/50 line-clamp-2">{description}</div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Date & Venue */}
                        <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-blue-400" />
                              <span className="text-[13px] text-white" style={{ fontWeight: 700 }}>Date & Venue</span>
                            </div>
                            <button onClick={() => setStep(1)} className="text-[11px] text-blue-400 hover:text-blue-300 transition-colors" style={{ fontWeight: 600 }}>
                              Edit
                            </button>
                          </div>
                          <div className="flex gap-8">
                            <div>
                              <div className="text-[10px] text-white/25 uppercase tracking-wider mb-0.5" style={{ fontWeight: 700 }}>Date</div>
                              <div className="text-[13px] text-white/70">{eventDate || 'Not set'}</div>
                            </div>
                            <div>
                              <div className="text-[10px] text-white/25 uppercase tracking-wider mb-0.5" style={{ fontWeight: 700 }}>Time</div>
                              <div className="text-[13px] text-white/70">{startTime || 'Not set'}{endTime ? ` – ${endTime}` : ''}</div>
                            </div>
                            <div>
                              <div className="text-[10px] text-white/25 uppercase tracking-wider mb-0.5" style={{ fontWeight: 700 }}>Venue</div>
                              <div className="text-[13px] text-white/70">{isOnline ? 'Online Event' : (venue || 'Not set')}</div>
                            </div>
                          </div>
                        </div>

                        {/* Tickets Summary */}
                        <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                              <Ticket className="w-4 h-4 text-blue-400" />
                              <span className="text-[13px] text-white" style={{ fontWeight: 700 }}>Tickets ({tiers.length} tiers)</span>
                            </div>
                            <button onClick={() => setStep(2)} className="text-[11px] text-blue-400 hover:text-blue-300 transition-colors" style={{ fontWeight: 600 }}>
                              Edit
                            </button>
                          </div>
                          <div className="space-y-2">
                            {tiers.map(tier => (
                              <div key={tier.id} className="flex items-center justify-between py-2.5 px-4 rounded-xl bg-white/[0.02]">
                                <div className="flex items-center gap-3">
                                  <div className="w-1.5 h-6 rounded-full bg-blue-500" />
                                  <div>
                                    <span className="text-[13px] text-white" style={{ fontWeight: 600 }}>{tier.ticketName}</span>
                                    <span className="text-[11px] text-white/25 ml-3">× {tier.quantity}</span>
                                  </div>
                                </div>
                                <span className="text-[14px] text-white" style={{ fontWeight: 700 }}>
                                  {(parseFloat(tier.price) || 0) === 0 ? 'Free' : `$${tier.price}`}
                                </span>
                              </div>
                            ))}
                          </div>
                          <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/[0.06]">
                            <span className="text-[12px] text-white/40" style={{ fontWeight: 600 }}>Total Capacity</span>
                            <span className="text-[16px] text-white" style={{ fontWeight: 800 }}>{totalCapacity.toLocaleString()} tickets</span>
                          </div>
                        </div>
                      </div>

                      {/* Publish Button */}
                      <div className="p-6 rounded-2xl bg-gradient-to-r from-[#E52324]/10 to-[#E52324]/5 border border-[#E52324]/20">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-[#E52324]/15 flex items-center justify-center shrink-0">
                            <Sparkles className="w-6 h-6 text-[#E52324]" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-[16px] text-white mb-1" style={{ fontWeight: 700 }}>Ready to go live?</h3>
                            <p className="text-[13px] text-white/40 mb-4">
                              Your event will be published to the InstaPass marketplace and visible to millions of fans. You can edit details anytime from your dashboard.
                            </p>
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={handlePublish}
                                className="flex items-center gap-2 px-8 py-3.5 rounded-2xl bg-[#E52324] text-white text-[14px] hover:bg-[#c91f20] transition-colors shadow-lg shadow-[#E52324]/25"
                                style={{ fontWeight: 800, letterSpacing: '0.04em' }}
                              >
                                <Send className="w-4 h-4" /> Publish Event
                              </button>
                              <button
                                onClick={handleSaveDraft}
                                className="flex items-center gap-2 px-5 py-3.5 rounded-2xl border border-white/10 text-white/50 text-[13px] hover:bg-white/5 transition-all"
                                style={{ fontWeight: 600 }}
                              >
                                <Save className="w-4 h-4" /> Save as Draft
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* ── Navigation Buttons ── */}
              {step < 4 && (
                <div className="flex items-center justify-between mt-10 pt-8 border-t border-white/[0.06]">
                  <button
                    onClick={goBack}
                    disabled={step === 0}
                    className="flex items-center gap-2 px-5 py-3 rounded-xl text-[13px] text-white/40 hover:text-white/70 hover:bg-white/5 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                    style={{ fontWeight: 600 }}
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    className={`flex items-center gap-2 px-7 py-3 rounded-xl text-white text-[13px] transition-all shadow-lg ${
                      canAdvance()
                        ? 'bg-blue-500 hover:bg-blue-600 shadow-blue-500/20 cursor-pointer'
                        : 'bg-blue-500/40 shadow-blue-500/10 cursor-pointer'
                    }`}
                    style={{ fontWeight: 700, letterSpacing: '0.03em' }}
                  >
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── Live Preview Panel ── */}
          <AnimatePresence>
            {showPreview && (
              <motion.aside
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 380, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="hidden md:flex flex-col shrink-0 border-l border-[#1E293B]/60 bg-[#0A1220] overflow-y-auto overflow-x-hidden"
              >
                <div className="p-6">
                  {/* Preview Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-blue-400" />
                      <span className="text-[12px] text-white/50" style={{ fontWeight: 700 }}>LIVE PREVIEW</span>
                    </div>
                    <button onClick={() => setShowPreview(false)} className="p-1.5 rounded-lg hover:bg-white/5 text-white/30 hover:text-white/60 transition-all">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Preview Card */}
                  <div className="rounded-2xl overflow-hidden bg-[#111827] border border-[#1F2937] shadow-2xl">
                    {/* Cover Image */}
                    <div className="relative aspect-[16/9] bg-[#0B1220]">
                      {coverImage ? (
                        <img src={coverImage} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-10 h-10 text-white/10" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#111827] via-transparent to-transparent" />
                      {selectedCategory && (
                        <span
                          className="absolute top-3 left-3 px-2.5 py-1 rounded-lg text-[9px] text-white uppercase tracking-wider"
                          style={{ fontWeight: 800, backgroundColor: selectedCategory.color }}
                        >
                          {selectedCategory.label}
                        </span>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-5">
                      <h3 className="text-[16px] text-white leading-tight mb-2" style={{ fontWeight: 800 }}>
                        {eventName || 'Your Event Name'}
                      </h3>

                      {eventDate && (
                        <div className="flex items-center gap-1.5 text-[11px] text-white/40 mb-1.5">
                          <Calendar className="w-3 h-3 text-[#E52324]" />
                          <span>{eventDate}{startTime ? ` · ${startTime}` : ''}</span>
                        </div>
                      )}

                      {(venue || isOnline) && (
                        <div className="flex items-center gap-1.5 text-[11px] text-white/40 mb-3">
                          <MapPin className="w-3 h-3 text-[#E52324]" />
                          <span>{isOnline ? 'Online Event' : venue}{city ? `, ${city}` : ''}</span>
                        </div>
                      )}

                      {description && (
                        <p className="text-[11px] text-white/30 leading-relaxed mb-4 line-clamp-3">
                          {description}
                        </p>
                      )}

                      {/* Tags */}
                      {tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {tags.map(tag => (
                            <span key={tag} className="px-2 py-0.5 rounded-md bg-white/5 text-[9px] text-white/30" style={{ fontWeight: 600 }}>
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Ticket Preview */}
                      {tiers.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {tiers.slice(0, 3).map(tier => (
                            <div key={tier.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/[0.03] border border-white/[0.05]">
                              <div>
                                <div className="text-[11px] text-white" style={{ fontWeight: 600 }}>{tier.ticketName}</div>
                                {tier.description && <div className="text-[9px] text-white/25 mt-0.5 line-clamp-1">{tier.description}</div>}
                              </div>
                              <span className="text-[13px] text-white" style={{ fontWeight: 800 }}>
                                {(parseFloat(tier.price) || 0) === 0 ? 'Free' : `$${tier.price}`}
                              </span>
                            </div>
                          ))}
                          {tiers.length > 3 && (
                            <div className="text-[10px] text-white/20 text-center py-1">+{tiers.length - 3} more tiers</div>
                          )}
                        </div>
                      )}

                      {/* CTA */}
                      <button className="w-full py-3 rounded-xl bg-[#E52324] text-white text-[12px] hover:bg-[#c91f20] transition-colors" style={{ fontWeight: 800, letterSpacing: '0.05em' }}>
                        Get Tickets — {priceRange}
                      </button>

                      {/* Trust badges */}
                      <div className="flex items-center justify-center gap-4 mt-3">
                        <span className="flex items-center gap-1 text-[9px] text-white/20">
                          <Check className="w-2.5 h-2.5 text-emerald-500" /> 100% Guaranteed
                        </span>
                        <span className="flex items-center gap-1 text-[9px] text-white/20">
                          <Zap className="w-2.5 h-2.5 text-amber-500" /> Instant Delivery
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* How it looks info */}
                  <div className="mt-4 p-3 rounded-xl bg-blue-500/5 border border-blue-500/10">
                    <div className="flex items-start gap-2">
                      <Info className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" />
                      <p className="text-[10px] text-white/30 leading-relaxed">
                        This is a preview of how your event will appear on the InstaPass marketplace. Final design may vary.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.aside>
            )}
          </AnimatePresence>
        </div>
      </div>

      {showPaymentModal && (
        <div
          className="fixed inset-0 z-[9999] overflow-y-auto bg-black/70 p-4 sm:p-6"
          onClick={() => !isPublishing && setShowPaymentModal(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Event creation fee"
        >
          <div className="flex min-h-full items-start justify-center py-4 sm:py-6">
            <div
              className="flex w-full max-w-md max-h-[min(92vh,calc(100vh-2rem))] flex-col overflow-hidden rounded-2xl border border-[#1F2937] bg-[#111827] shadow-2xl shadow-black/50"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-shrink-0 items-center justify-between border-b border-[#1F2937] px-5 py-4">
                <h3 className="text-lg font-bold text-white">Event creation fee</h3>
                <button
                  type="button"
                  onClick={() => !isPublishing && setShowPaymentModal(false)}
                  aria-label="Close"
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <HiX size={24} />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-5 py-4 [-webkit-overflow-scrolling:touch]">
                <p className="mb-4 text-sm text-white/50">
                  A one-time fee of <strong className="text-white">${EVENT_CREATION_FEE}.00</strong> is required to publish your event.
                </p>
                {paymentError && <p className="mb-3 text-sm text-red-400">{paymentError}</p>}
                <StripePayment
                  show={showPaymentModal}
                  onPaymentCompleted={handlePaymentCompleted}
                  totalamount={EVENT_CREATION_FEE}
                  setShow={setShowPaymentModal}
                  setError={setPaymentError}
                  scrollMode="parent"
                />
                {isPublishing && (
                  <p className="mt-3 text-center text-sm text-white/40">Creating your event…</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
